import React, { useState } from 'react'

import { debounce } from 'lodash'
import { nanoid } from 'nanoid'

import { Condition, FilterType } from 'types/GlobalKpiOption'
import {
  TSlice,
  TSliceFilterCondition,
  TSliceFilterConditionType
} from 'types/GlobalSlice'

import { isOnlyLeftFilter } from 'utils/functions'
import { sortAlphabeticalAsc } from 'utils/sortHelper'

import Box from 'components_new/atoms/Box'
import Chip from 'components_new/atoms/Chip'
import Divider from 'components_new/atoms/Divider'
import Icon from 'components_new/atoms/Icon'

import ContentBox from 'components_new/molecules/ContentBox'
import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'
import { AiIconTip } from 'components_new/molecules/IconTip'
import ListItemButton from 'components_new/molecules/ListItemButton'
import OptionsMenuButton from 'components_new/molecules/OptionsMenuButton'

import FilterItem from 'components_new/organisms/FilterItem'
import DeleteSliceDialog from 'components_new/organisms/dialogs/DeleteSliceDialog'

import { useSliceActions } from 'redux/actions/Slices'
import { useEntity } from 'redux/hooks/Entities'
import { useTranslation } from 'language/translator'
import { useWidgetActions } from 'redux/actions/Widgets'

interface Props {
  defaultExpanded?: boolean
  id: string
  edit: boolean
  slice: TSlice
}

type TStateFilter = {
  id: string
  entity_column_id: string | null
  condition: TSliceFilterCondition
  values: string[]
  filter_group_id: string | null
}
type TValidStateFilter = TStateFilter & { entity_column_id: string }

interface IValidFilterBody {
  entity_column_id: string
  type: TSliceFilterConditionType
  condition: TSliceFilterCondition
  right_value: string | null
  filter_group_id: string | null
}

// A single filter, or a group of filters that are OR:ed together.
type TFilterBlock = {
  groupId: string | null
  filters: TStateFilter[]
}

const getInitFilters = (slice: TSlice) => {
  const init: { [key: string]: TStateFilter } = {}

  slice.filters.forEach((filter) => {
    const localId = nanoid()

    init[localId] = {
      id: localId,
      ...filter
    }
  })

  return init
}

const validateFilter = (filter: TStateFilter): filter is TValidStateFilter => {
  return (
    !!filter.entity_column_id &&
    (isOnlyLeftFilter(filter.condition) || filter.values.length > 0)
  )
}

// Lay the flat filter list out as blocks, keeping the order of the
// list so a group appears where its first filter is.
const getFilterBlocks = (filters: {
  [key: string]: TStateFilter
}): TFilterBlock[] => {
  const blocks: TFilterBlock[] = []
  const groupBlocks: { [groupId: string]: TFilterBlock } = {}

  Object.values(filters).forEach((filter) => {
    if (!filter.filter_group_id) {
      blocks.push({ groupId: null, filters: [filter] })

      return
    }

    const existing = groupBlocks[filter.filter_group_id]

    if (existing) {
      existing.filters.push(filter)

      return
    }

    const block = { groupId: filter.filter_group_id, filters: [filter] }

    groupBlocks[filter.filter_group_id] = block
    blocks.push(block)
  })

  return blocks
}

const EntitySliceRow = (props: Props) => {
  const { defaultExpanded, edit, slice, id } = props

  // Product-defined slices (read_only on the API response) are managed
  // entirely from code — the filter editor renders as static.
  const editable = edit && !slice.read_only

  const entity = useEntity(id)
  const { t } = useTranslation()

  const { tryUpdateSlice } = useSliceActions()
  const { invalidateWidgets } = useWidgetActions()

  const [deleteFilterDialog, setDeleteFilterDialog] = useState<{
    open: boolean
    value: TStateFilter | null
  }>({ open: false, value: null })

  const handleOpenDeleteFilter = (filter: TStateFilter) => {
    setDeleteFilterDialog({ open: true, value: filter })
  }

  const handleCloseDeleteFilter = () => {
    setDeleteFilterDialog({ ...deleteFilterDialog, open: false })

    debounce(() => {
      setDeleteFilterDialog({ ...deleteFilterDialog, value: null })
    }, 300)
  }

  const [filters, setFilters] = useState(getInitFilters(slice))

  const isNumericColumn = (columnId: string | null) =>
    (columnId && entity?.filterOptions[columnId]?.numeric) ?? false

  // update slice with all valid filters
  const putValidFilters = (newFilters: { [key: string]: TStateFilter }) => {
    const validFilters = Object.values(newFilters)
      .filter(validateFilter)
      .flatMap<IValidFilterBody>((filter) => {
        if (isOnlyLeftFilter(filter.condition)) {
          return [
            {
              entity_column_id: filter.entity_column_id,
              type: FilterType.ONLY_LEFT,
              condition: filter.condition,
              right_value: null,
              filter_group_id: filter.filter_group_id
            }
          ]
        }

        if (isNumericColumn(filter.entity_column_id)) {
          return [
            {
              entity_column_id: filter.entity_column_id,
              type: FilterType.NUMBER,
              condition: filter.condition,
              right_value: filter.values[0],
              filter_group_id: filter.filter_group_id
            }
          ]
        }

        return filter.values.map((value) => ({
          entity_column_id: filter.entity_column_id,
          type: FilterType.INPUT_VALUE,
          condition: filter.condition,
          right_value: value,
          filter_group_id: filter.filter_group_id
        }))
      })

    tryUpdateSlice(slice.id, { filters: validFilters })

    // When we change a slice data might not be valid anymore.
    invalidateWidgets()
  }

  const handleDeleteFilter = (filter: TStateFilter) => {
    const newFilters = { ...filters }

    const deletedFilter = { ...newFilters[filter.id] }

    delete newFilters[filter.id]

    setFilters(newFilters)
    if (validateFilter(deletedFilter)) {
      // only put if changed filter is valid.
      putValidFilters(newFilters)
    }

    handleCloseDeleteFilter()
  }

  const handleChangeCondition = (
    filter: TStateFilter,
    condition: TSliceFilterCondition
  ) => {
    const changedFilter = {
      ...filters[filter.id],
      condition
    }

    if (isOnlyLeftFilter(condition)) {
      // clear values
      changedFilter.values = []
    }

    const newFilters = {
      ...filters,
      [filter.id]: changedFilter
    }

    setFilters(newFilters)
    if (validateFilter(changedFilter)) {
      // only put if changed filter is valid.
      putValidFilters(newFilters)
    }
  }

  const handleChangeEntityColumn = (
    filter: TStateFilter,
    entityColumnId: string
  ) => {
    const changedFilter = {
      ...filters[filter.id],
      entity_column_id: entityColumnId
    }

    if (filters[filter.id].entity_column_id !== entityColumnId) {
      // changed column --> reset values
      changedFilter.values = []

      const numericConditions: TSliceFilterCondition[] = [
        Condition.GT,
        Condition.LT
      ]

      if (
        !isNumericColumn(entityColumnId) &&
        numericConditions.includes(changedFilter.condition)
      ) {
        // numeric-only condition on a value-list column --> reset
        changedFilter.condition = Condition.EQ
      }
    }

    const newFilters = {
      ...filters,
      [filter.id]: changedFilter
    }

    setFilters(newFilters)

    if (validateFilter(changedFilter)) {
      // only put if changed filter is valid.
      putValidFilters(newFilters)
    }
  }

  const handleChangeValues = (filter: TStateFilter, values: string[]) => {
    const changedFilter = {
      ...filters[filter.id],
      values
    }

    const newFilters = {
      ...filters,
      [filter.id]: changedFilter
    }

    setFilters(newFilters)

    if (validateFilter(changedFilter)) {
      // only put if changed filter is valid.
      putValidFilters(newFilters)
    }
  }

  const handleAddFilters = (groupId: string | null, amount: number) => {
    setFilters((prev) => {
      const added: { [key: string]: TStateFilter } = {}

      for (let i = 0; i < amount; i++) {
        const localId = nanoid()

        added[localId] = {
          id: localId,
          entity_column_id: null,
          condition: Condition.EQ,
          values: [],
          filter_group_id: groupId
        }
      }

      return { ...prev, ...added }
    })
  }

  if (!entity) {
    return null
  }

  const columnOptions = Object.values(entity.filterOptions)
    .map((column) => ({
      id: column.id,
      title: column.name,
      endAdornment: entity.columnMapper.get(column.id)?.ai_generated ? (
        <AiIconTip title={t('global', 'ai_generated')} />
      ) : undefined
    }))
    .sort((a, b) => sortAlphabeticalAsc(a.title, b.title))

  const renderFilterItem = (filter: TStateFilter) => {
    const entityColumn = filter.entity_column_id
      ? entity.columnMapper.get(filter.entity_column_id) ?? null
      : null

    const filterOption = entityColumn
      ? entity.filterOptions[entityColumn.id]
      : null

    const valueOptions = filterOption
      ? filterOption.options.map((value) => ({
        id: value,
        title: value
      }))
      : []

    return (
      <FilterItem
        attributeOptions={columnOptions}
        attributeSelected={
          entityColumn
            ? {
                id: entityColumn.id,
                title: entityColumn.name
              }
            : null
        }
        conditionSelected={filter.condition}
        editable={editable}
        disablePadding={true}
        numeric={filterOption?.numeric ?? false}
        onChangeAttribute={(columnId) =>
          handleChangeEntityColumn(filter, columnId)
        }
        onChangeCondition={(condition) =>
          handleChangeCondition(filter, condition)
        }
        onChangeValue={(values) => handleChangeValues(filter, values)}
        onDelete={() => handleOpenDeleteFilter(filter)}
        valueOptions={valueOptions}
        valueSelected={filter.values}
        variant="transparent"
      />
    )
  }

  const numberOfFilters = Object.keys(filters).length
  const invalid = Object.values(filters).some(
    (filter) => !validateFilter(filter)
  )

  return (
    <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
      <ContentBox
        collapse={{
          active: true,
          defaultCollapsed: !defaultExpanded
        }}
        gutterBottom={true}
        header={{
          icon: { name: 'Slice' },
          title: slice.name,
          titleAdornment:
            numberOfFilters === 0 ? (
              <Chip
                color="warning"
                icon={<Icon name="Error" />}
                label={t('entities', 'not_configured')}
                size="xsmall"
              />
            ) : invalid ? (
              <Chip
                color="error"
                icon={<Icon name="Error" />}
                label={t('entities', 'not_ready')}
                size="xsmall"
              />
            ) : (
              <Chip label={`${numberOfFilters} filter`} size="xsmall" />
            )
        }}
        size="small"
      >
        {numberOfFilters === 0 ? (
          <EmptyPlaceholder
            background={true}
            title={t('filters', 'no_filters')}
            sx={{ ['&:not(:only-child)']: { mb: 1 } }}
          />
        ) : (
          getFilterBlocks(filters).map((block) => {
            const items = block.filters.map((filter, i) => (
              <React.Fragment key={filter.id}>
                {i > 0 ? (
                  <Divider sx={{ mb: 1 }}>
                    <Chip label={t('filters', 'or')} size="xsmall" />
                  </Divider>
                ) : null}
                {renderFilterItem(filter)}
              </React.Fragment>
            ))

            if (!block.groupId) {
              return items
            }

            return (
              <Box
                key={block.groupId}
                sx={{
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 1,
                  p: 1
                }}
              >
                {items}
                {editable ? (
                  <ListItemButton
                    icon={{ name: 'Add' }}
                    onClick={() => handleAddFilters(block.groupId, 1)}
                    size="small"
                    title={t('filters', 'add_filter')}
                  />
                ) : null}
              </Box>
            )
          })
        )}
        {editable ? (
          <Box sx={{ alignItems: 'center', display: 'flex' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <ListItemButton
                icon={{ name: 'Add' }}
                onClick={() => handleAddFilters(null, 1)}
                size="small"
                title={t('filters', 'add_filter')}
              />
            </Box>
            <OptionsMenuButton
              MenuProps={{
                sx: { zIndex: 1302 } // above the slices dialogs
              }}
              options={[
                {
                  iconName: 'Add',
                  onClick: () => handleAddFilters(nanoid(), 2),
                  title: t('filters', 'add_filter_group')
                }
              ]}
              size="small"
            />
          </Box>
        ) : null}
      </ContentBox>
      <DeleteSliceDialog
        handleClose={handleCloseDeleteFilter}
        handleDelete={(callback) => {
          if (deleteFilterDialog.value) {
            handleDeleteFilter(deleteFilterDialog.value)
            callback()
          }
        }}
        open={deleteFilterDialog.open}
        slice={slice}
        type="filter"
      />
    </Box>
  )
}

export default EntitySliceRow
